import type { ReactElement } from "react";
import { useId, useState } from "react";

import type { Patient } from "@elation/api-client/src/stitch-web/models";

import { PRODUCT_NAME } from "@src/shared/brand";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@src/shared/components/ui/dialog";

import type { CreatePatientFormValues } from "../../../../domain";
import {
  CreatePatientFormV2,
  useCreatePatientFormSubmission,
} from "../CreatePatientForm";

export interface CreatePatientDialogV2Props {
  canCreatePatient?: boolean;
  children?: ReactElement;
  defaultOpen?: boolean;
  initialValues?: Partial<CreatePatientFormValues>;
  onCreated?: (patient: Patient | null) => Promise<void> | void;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
}

export function CreatePatientDialogV2({
  canCreatePatient = true,
  children,
  defaultOpen = false,
  initialValues,
  onCreated,
  onOpenChange,
  open,
}: CreatePatientDialogV2Props) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const resolvedOpen = open ?? uncontrolledOpen;
  const generatedId = useId();
  const titleId = `${generatedId}-title`;
  const descriptionId = `${generatedId}-description`;
  const { error, isSubmitting, reset, submitFormValues } =
    useCreatePatientFormSubmission({ onCreated });

  const handleOpenChange = (nextOpen: boolean) => {
    if (isSubmitting && !nextOpen) return;
    setUncontrolledOpen(nextOpen);
    onOpenChange?.(nextOpen);
    if (!nextOpen) reset();
  };

  const handleSubmit = async (values: CreatePatientFormValues) => {
    await submitFormValues(values);
    handleOpenChange(false);
  };

  if (!canCreatePatient) return null;

  return (
    <Dialog open={resolvedOpen} onOpenChange={handleOpenChange}>
      {children ? <DialogTrigger asChild>{children}</DialogTrigger> : null}
      <DialogContent
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        showOverlay={false}
        className="w-[min(calc(100vw-2rem),560px)] rounded-[18px] border-border p-5 shadow-soft"
      >
        <DialogHeader className="space-y-0 text-left">
          <DialogTitle
            id={titleId}
            className="text-[20px] font-semibold leading-none tracking-[-0.02em] text-surface-foreground"
          >
            New patient
          </DialogTitle>
          <DialogDescription id={descriptionId} className="sr-only">
            Enter the patient&apos;s demographics to create their{" "}
            {PRODUCT_NAME.singular} record.
          </DialogDescription>
        </DialogHeader>

        <CreatePatientFormV2
          error={error}
          initialValues={initialValues}
          isSubmitting={isSubmitting}
          onCancel={() => handleOpenChange(false)}
          onSubmit={handleSubmit}
        />
      </DialogContent>
    </Dialog>
  );
}
